import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { EntityConnectorInterface } from 'app/core/connector-services/entity-connector-service.interface';
import { RestConnectorService } from 'app/core/connector-services/rest-connector-service/rest-connector-service.abstract';
import { UserDetailedInterface, UserInterface } from 'app/core/user/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ErgoTeamMemberInterface } from '../models/ergo-team-member.model';

@Injectable()
export class UserRestConnectorService extends RestConnectorService implements EntityConnectorInterface<UserInterface> {

  constructor(private readonly _httpClient: HttpClient,
              protected readonly _store: Store<AppStateInterface>) {
    super(_store);
    this.onInit();
  }

  public onInit(): void {}

  public loadAll(): Observable<UserInterface[]> {
    return this._httpClient.get<UserInterface[]>(
      '/api/users',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<UserInterface[]>) => <UserInterface[]>httpResponse.body),
    );
  }

  public loadAllDetailed(): Observable<UserDetailedInterface[]> {
    return this._httpClient.get<UserDetailedInterface[]>(
      '/api/systemusers/detailed',
      { observe: 'response' },
    )
    .pipe(
       map((httpResponse: HttpResponse<UserDetailedInterface[]>) => <UserDetailedInterface[]>httpResponse.body),
    );
  }

  public loadAllAdmin(): Observable<UserInterface[]> {
    return this._httpClient.get<UserInterface[]>(
      '/api/users/admin',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<UserInterface[]>) => <UserInterface[]>httpResponse.body),
    );
  }

  public loadOne(id: number): Observable<UserInterface> {
    return this._httpClient.get<UserInterface>(
      `/api/users/${id}`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<UserInterface>) => <UserInterface>httpResponse.body),
    );
  }

  public loadOneDetailed(id: number): Observable<UserDetailedInterface> {
    return this._httpClient.get<UserDetailedInterface>(
      `/api/users/${id}`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<UserDetailedInterface>) => httpResponse.body),
    );
  }

  public loadUserErgoTeam(user: UserInterface): Observable<UserInterface> {
    return this._httpClient.get<ErgoTeamMemberInterface[]>(
     `/api/users/${user.id}/ergo-team`,
     { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<ErgoTeamMemberInterface[]>) => httpResponse.body),
      map((ergoTeam: ErgoTeamMemberInterface[]) => {
        const updatedUser: UserInterface = {
          ...user,
          ergoTeam,
        };
        return updatedUser;
      }),
    );
  }

  public addUserErgoTeamMember(user: UserInterface, memberToAdd: ErgoTeamMemberInterface): Observable<UserInterface> {
    return this._httpClient.post<ErgoTeamMemberInterface[]>(
      `/api/users/${user.id}/ergo-team`,
      memberToAdd,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<ErgoTeamMemberInterface[]>) => httpResponse.body),
      map((ergoTeam: ErgoTeamMemberInterface[]) => {
        const updatedUser: UserInterface = {
          ...user,
          ergoTeam,
        };
        return updatedUser;
      }),
    );
  }

  public removeUserErgoTeamMember(user: UserInterface, memberToAdd: ErgoTeamMemberInterface): Observable<UserInterface> {
    return this._httpClient.delete<ErgoTeamMemberInterface[]>(
      `/api/users/${user.id}/ergo-team/${memberToAdd.memberUser.id}`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<ErgoTeamMemberInterface[]>) => httpResponse.body),
      map((ergoTeam: ErgoTeamMemberInterface[]) => {
        const updatedUser: UserInterface = {
          ...user,
          ergoTeam,
        };
        return updatedUser;
      }),
    );
  }

  public deleteUser(userToDelete: UserInterface): Observable<UserInterface> {
    return this._httpClient.delete<UserInterface>(
      `/api/users/${userToDelete.id}`,
      { observe: 'response' },
    )
    .pipe(
      map(() => userToDelete),
    );
  }

  public update(entity: UserInterface): Observable<UserInterface> {
    const httpHeaders: HttpHeaders = this.getHttpHeaders();
    const userId: string = entity.id.toString();

    return this._httpClient.put<UserInterface>(
      `/api/users/${userId}`,
      entity,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    )
    .pipe(
      map((httpResponse: HttpResponse<UserInterface>) => <UserInterface>httpResponse.body),
    );
  }

  public createNewUser(userToCreate: UserInterface): Observable<UserInterface> {
    return this._httpClient.post<UserInterface>(
      '/api/users/',
      userToCreate,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<UserInterface>) => httpResponse.body),
    );
  }

  public checkUserName(nameToCheck: string): Observable<boolean> {
    const sendObj: {name: string} = {
      name: nameToCheck,
    };
    return this._httpClient.post<boolean>(
      'api/users/check-user-name',
      sendObj,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<boolean>) => httpResponse.body),
    );
  }

}
